import React, { useRef, useState } from 'react'
import {
  Upload,
  FolderInput,
  FileText,
  Trash2,
  RotateCw,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Eye,
  EyeOff,
  X,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import Button from '../components/ui/Button'
import DocumentViewer from '../components/DocumentViewer'
import { langLabel } from '../components/ProjectBar'
import { useProjects, useDocuments, useAddPath, useDeleteDocument, useReindexDocument, useRefreshStale, useCurateDocument, useScanCodePath, useImportCodebase, useCloneCodebase, useCodebaseStatus, useSyncCodebase, useSetCodebaseAutoSync } from '../hooks/useRag'
import { useLinkHealth, useBuildCodeGraph, useCodeRepos, useUnbindCodeRepo } from '../hooks/useResearch'
import { useSaga } from '../hooks/useImport'
import ConfluenceImport from '../components/ConfluenceImport'
import { uploadDocuments } from '../services/ragApi'

const ACCEPT = '.txt,.pdf,.docx,.md,.srt,.csv,.json'

// Staleness sweep: compare file mtimes vs indexed_at — re-embed only what
// changed, report files that vanished. Keeps a living corpus honest.
function RefreshStaleButton({ project }) {
  const sweep = useRefreshStale()
  const d = sweep.data
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <button
        onClick={() => sweep.mutate({ project })}
        disabled={sweep.isPending}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-50 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
        title="Re-index documents whose source files changed since indexing; report missing files"
      >
        {sweep.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
        Refresh stale
      </button>
      {d && (
        <span
          className={d.missing.length ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-300'}
          title={d.missing.join('\n') || undefined}
        >
          {d.reindexed} re-indexed · {d.unchanged} fresh
          {d.missing.length > 0 && ` · ${d.missing.length} missing`}
        </span>
      )}
      {sweep.isError && <span className="text-red-600">{sweep.error?.message}</span>}
    </span>
  )
}

// On-demand probe of every indexed source URL (dead videos, removed pages).
function LinkHealthButton({ project }) {
  const check = useLinkHealth()
  const d = check.data
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <button
        onClick={() => check.mutate({ project })}
        disabled={check.isPending}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-50 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
        title="Probe every indexed source URL (YouTube/Rutube/web) and report dead links"
      >
        {check.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
        Check links
      </button>
      {d && (
        <span
          className={d.dead.length ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-300'}
          title={d.dead.map((x) => `${x.url} (HTTP ${x.status})`).join('\n') || undefined}
        >
          {d.dead.length
            ? `${d.dead.length} dead of ${d.checked} checked`
            : `all ${d.checked} links alive`}
          {d.skipped > 0 && <span className="text-ink-soft dark:text-mist/50"> · {d.skipped} without URL</span>}
        </span>
      )}
      {check.isError && <span className="text-red-600">{check.error?.message}</span>}
    </span>
  )
}

// Library/Files panel for the Sources tab. The project is owned by SourcesPage's
// shared ProjectBar and passed in; this renders the document list (view=library)
// and/or the add controls (view=add).
const DOC_PAGE = 50 // documents per page (server-side)

export default function DocumentsPage({ embedded = false, view = 'all', project = '' }) {
  const projectsQuery = useProjects()
  const [dragging, setDragging] = useState(false)
  const [viewer, setViewer] = useState(null)
  const fileInput = useRef(null)

  const qc = useQueryClient()
  const addFolder = useAddPath()
  const removeDoc = useDeleteDocument()
  const reindexDoc = useReindexDocument()
  const curateDoc = useCurateDocument()
  const [docOffset, setDocOffset] = useState(0)
  const docsQuery = useDocuments(project, { limit: DOC_PAGE, offset: docOffset })
  // reset to the first page when the project changes
  React.useEffect(() => { setDocOffset(0) }, [project])
  const [pathInput, setPathInput] = useState('')
  const [codePathInput, setCodePathInput] = useState('')
  // Codebase import: Scan (dry-run routing manifest) → Import (durable saga).
  const scanCode = useScanCodePath()
  const importCode = useImportCodebase()
  const [codeSagaId, setCodeSagaId] = useState(null)
  // Adding a repo from OUTSIDE this machine: clone a git URL into the
  // workspace, then the normal index flow. The local-path box above needs the
  // code to already be on the host, which is useless from a phone.
  const [cloneUrl, setCloneUrl] = useState('')
  const cloneCode = useCloneCodebase()
  const codeSaga = useSaga(codeSagaId)
  // Scan-type choices for the import (one-flow "add a repo as a source")
  const [importTarget, setImportTarget] = useState('current') // current | new
  const [newProjName, setNewProjName] = useState('')
  const [optGraph, setOptGraph] = useState(true)
  const [optWatch, setOptWatch] = useState(false)
  // Git-aware sync: Check changes (status) → Sync now (saga).
  const gitStatus = useCodebaseStatus()
  const syncCode = useSyncCodebase()
  const autoSync = useSetCodebaseAutoSync()
  const [syncSagaId, setSyncSagaId] = useState(null)
  const syncSaga = useSaga(syncSagaId)
  const refreshCode = useRefreshStale()
  const buildGraph = useBuildCodeGraph()
  const codeReposQ = useCodeRepos(project)
  const unbindRepo = useUnbindCodeRepo()
  const [imp, setImp] = useState(null) // batched browser-upload progress
  const [translateOn, setTranslateOn] = useState(true) // translate mismatched files

  const documents = docsQuery.data?.documents ?? []
  const projLang = (projectsQuery.data?.projects ?? []).find((p) => p.name === project)?.language || 'auto'
  const hasLang = projLang && projLang !== 'auto'

  // An already-imported repo shouldn't need retyping: prefill the path from
  // the project's bound repos (and clear a stale prefill on project switch).
  const boundRepos = codeReposQ.data?.code_repos ?? []
  React.useEffect(() => {
    if (!codePathInput.trim() && boundRepos.length) setCodePathInput(boundRepos[0])
  }, [boundRepos, codePathInput])
  React.useEffect(() => {
    // project changed → previous prefill no longer applies; let the effect
    // above refill from the new project's bindings
    setCodePathInput('')
    scanCode.reset()
    setCodeSagaId(null)
    setSyncSagaId(null)
    gitStatus.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  // When a codebase saga (import or sync) settles, pull fresh documents/repos.
  const codeSagaStatus = codeSaga.data?.status
  const syncSagaStatus = syncSaga.data?.status
  React.useEffect(() => {
    if (codeSagaStatus === 'completed' || syncSagaStatus === 'completed') {
      qc.invalidateQueries({ queryKey: ['rag', 'documents'] })
      qc.invalidateQueries({ queryKey: ['rag', 'projects'] })
      qc.invalidateQueries({ queryKey: ['research'] })
    }
  }, [codeSagaStatus, syncSagaStatus, qc])

  // Upload in small batches so a big drop can't blow up the browser tab.
  const doUpload = async (fileList) => {
    if (!project || !fileList?.length) return
    const files = [...fileList]
    const BATCH = 8
    const translateTo = hasLang && translateOn ? projLang : undefined
    setImp({ active: true, done: 0, total: files.length, indexed: 0, error: null, translated: 0 })
    for (let i = 0; i < files.length; i += BATCH) {
      const batch = files.slice(i, i + BATCH)
      try {
        const r = await uploadDocuments({ project, files: batch, translateTo })
        const didTranslate = (r.translations ?? []).filter((t) => t.translated).length
        setImp((s) => ({
          ...s,
          done: Math.min(s.done + batch.length, files.length),
          indexed: s.indexed + (r.chunks_indexed ?? 0),
          translated: (s.translated ?? 0) + didTranslate,
        }))
      } catch (e) {
        setImp((s) => ({ ...s, active: false, error: e.message }))
        return
      }
    }
    setImp((s) => ({ ...s, active: false }))
    qc.invalidateQueries({ queryKey: ['rag', 'documents', project] })
    qc.invalidateQueries({ queryKey: ['rag', 'projects'] })
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    doUpload(e.dataTransfer.files)
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <header>
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">The Archive</div>
          <h1 className="font-display text-4xl text-ink dark:text-mist">Documents</h1>
        </header>
      )}

      {!project && <Note kind="muted" text="Select a project above to continue." />}

      {project && view !== 'library' && (
        <>
          {/* Language-aware ingest toggle */}
          {hasLang && (
            <label className="flex items-start gap-2 text-sm text-ink dark:text-mist cursor-pointer">
              <input
                type="checkbox"
                checked={translateOn}
                onChange={(e) => setTranslateOn(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-parchment-border text-gold focus:ring-gold/50 dark:border-midnight-600"
              />
              <span>
                Translate sources that aren&apos;t in{' '}
                <span className="font-medium text-gold-deep dark:text-gold">{langLabel(projLang)}</span> into it before indexing
                <span className="block text-xs text-ink-soft dark:text-mist/50">
                  Keeps this project&apos;s corpus in one language. A file already in {langLabel(projLang)} is indexed as-is.
                </span>
              </span>
            </label>
          )}

          {/* Upload dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInput.current?.click()}
            className={[
              'rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition',
              dragging ? 'border-spark bg-spark/5' : 'border-parchment-border hover:border-gold dark:border-midnight-600 dark:hover:border-gold',
            ].join(' ')}
          >
            <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => doUpload(e.target.files)} />
            {imp?.active ? (
              <div className="flex items-center justify-center gap-2 text-ink-soft dark:text-mist/70">
                <Loader2 className="w-5 h-5 animate-spin" /> Indexing {imp.done}/{imp.total} into {project}…
              </div>
            ) : (
              <>
                <Upload className="w-6 h-6 mx-auto text-gold-deep dark:text-gold" />
                <p className="mt-2 text-ink dark:text-mist">
                  Drop files here, or <span className="text-gold-deep dark:text-gold">browse</span>
                </p>
                <p className="mt-1 text-xs text-ink-soft dark:text-mist/50">
                  {ACCEPT.replaceAll('.', '').replaceAll(',', ' · ')} — added to <span className="font-medium">{project}</span>
                </p>
              </>
            )}
          </div>

          {imp?.error && <Note kind="error" text={imp.error} />}
          {imp && !imp.active && !imp.error && imp.done > 0 && (
            <Note
              kind="ok"
              text={`Indexed ${imp.indexed} chunks from ${imp.done} file(s)${
                imp.translated ? ` · translated ${imp.translated} into ${langLabel(projLang)}` : ''
              }.`}
            />
          )}

          {/* Import a folder already on this machine — no upload (robust for big sets) */}
          <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-ink dark:text-mist">
              <FolderInput className="w-4 h-4 text-gold-deep dark:text-gold" />
              <span className="font-medium">Import a folder on this machine</span>
              <span className="text-xs text-ink-soft dark:text-mist/50">— no upload; best for large sets</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={pathInput}
                onChange={(e) => setPathInput(e.target.value)}
                placeholder="C:\path\to\documents"
                className="flex-1 h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
              <Button
                type="button"
                onClick={() => pathInput.trim() && addFolder.mutate({ project, path: pathInput.trim() })}
                loading={addFolder.isPending}
                disabled={!pathInput.trim() || !project}
              >
                Index folder
              </Button>
            </div>
            {addFolder.isPending && (
              <p className="text-xs text-ink-soft dark:text-mist/60">Indexing folder into {project}… this can take a while for large sets.</p>
            )}
            {addFolder.isError && <Note kind="error" text={addFolder.error?.message} hint={addFolder.error?.hint} />}
            {addFolder.isSuccess && (
              <Note
                kind="ok"
                text={`Indexed ${addFolder.data.chunks_indexed} chunks from ${addFolder.data.indexed_files}/${addFolder.data.found} files${
                  addFolder.data.errors?.length ? ` (${addFolder.data.errors.length} batch error(s))` : ''
                }.`}
              />
            )}
          </div>

          {/* C1 — codebase as a source: walk a repo (code extensions, junk
              dirs skipped), chunks split on declarations + tagged as code.
              Powers "how does it work today" questions and impact analysis. */}
          <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-ink dark:text-mist">
              <FileText className="w-4 h-4 text-gold-deep dark:text-gold" />
              <span className="font-medium">Index a codebase</span>
              <span className="text-xs text-ink-soft dark:text-mist/50">
                — scan detects supported files and routes them: all → vector index, analyzer languages → code graph
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={codePathInput}
                onChange={(e) => {
                  setCodePathInput(e.target.value)
                  // path changed → the last scan/import no longer describes it
                  scanCode.reset()
                  setCodeSagaId(null)
                }}
                placeholder="C:\path\to\repo"
                className="flex-1 h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
              <Button
                type="button"
                onClick={() => {
                  if (!codePathInput.trim()) return
                  setCodeSagaId(null)
                  scanCode.mutate(
                    { path: codePathInput.trim() },
                    {
                      onSuccess: (d) => {
                        // sensible defaults per repo: project name from the
                        // basename, graph when analyzable, watcher when git
                        setNewProjName(d.root.split(/[\\/]/).filter(Boolean).pop() || '')
                        setOptGraph(!!d.has_graph_lang)
                        setOptWatch(!!d.git?.is_git)
                      },
                    }
                  )
                }}
                loading={scanCode.isPending}
                disabled={!codePathInput.trim() || !project}
              >
                Scan repo
              </Button>
            </div>
            {/* …or fetch it first. Same destination, same index flow — this is
                the only path that works when the repository is not on this
                machine yet (a phone cannot produce a local path). */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={cloneUrl}
                onChange={(e) => { setCloneUrl(e.target.value); cloneCode.reset() }}
                placeholder="…or clone from https://github.com/owner/repo.git"
                className="flex-1 h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
              <Button
                type="button"
                onClick={() => {
                  const url = cloneUrl.trim()
                  if (!url || !project) return
                  setCodeSagaId(null)
                  cloneCode.mutate(
                    { url, project, graph: true, create_project: false },
                    { onSuccess: (d) => d?.saga_id && setCodeSagaId(d.saga_id) }
                  )
                }}
                loading={cloneCode.isPending}
                disabled={!cloneUrl.trim() || !project}
              >
                Clone &amp; index
              </Button>
            </div>
            {cloneCode.isError && <Note kind="error" text={cloneCode.error?.message} hint={cloneCode.error?.hint} />}
            {cloneCode.isSuccess && cloneCode.data?.destination && (
              <p className="text-xs text-ink-soft dark:text-mist/60">
                Cloning into <span className="font-mono">{cloneCode.data.destination}</span> — indexing follows automatically.
              </p>
            )}
            {scanCode.isPending && (
              <p className="text-xs text-ink-soft dark:text-mist/60">Detecting supported files under {codePathInput}…</p>
            )}
            {scanCode.isError && <Note kind="error" text={scanCode.error?.message} hint={scanCode.error?.hint} />}
            {/* Scan preview: what was detected and WHERE each language goes —
                everything supported → vector index; analyzer languages → also
                the Neo4j code graph (Graph RAG / ripple). */}
            {scanCode.isSuccess && !codeSagaId && (
              <div className="rounded-lg border border-parchment-border/70 dark:border-midnight-600/70 p-3 space-y-2">
                <div className="text-xs text-ink-soft dark:text-mist/60">
                  {scanCode.data.total_files} files · {fmtBytes(scanCode.data.total_bytes)} → vector index
                  {scanCode.data.has_graph_lang
                    ? ` · code graph: ${scanCode.data.graph_languages.join(', ')}`
                    : ' · code graph: none of the analyzer languages present'}
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-ink-soft dark:text-mist/50">
                      <th className="py-1 pr-2 font-medium">Language</th>
                      <th className="py-1 pr-2 font-medium">Files</th>
                      <th className="py-1 pr-2 font-medium">Size</th>
                      <th className="py-1 pr-2 font-medium">Vector</th>
                      <th className="py-1 font-medium">Graph</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanCode.data.languages.map((l) => (
                      <tr key={l.language} className="border-t border-parchment-border/40 dark:border-midnight-600/40">
                        <td className="py-1 pr-2 text-ink dark:text-mist" title={l.extensions.join(' ')}>{l.language}</td>
                        <td className="py-1 pr-2 text-ink dark:text-mist">{l.files}</td>
                        <td className="py-1 pr-2 text-ink-soft dark:text-mist/60">{fmtBytes(l.bytes)}</td>
                        <td className="py-1 pr-2 text-emerald-700 dark:text-emerald-300">✓</td>
                        <td className={l.graph ? 'py-1 text-emerald-700 dark:text-emerald-300' : 'py-1 text-ink-soft dark:text-mist/40'}>
                          {l.graph ? '✓' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {scanCode.data.unsupported?.length > 0 && (
                  <div className="text-[11px] text-ink-soft dark:text-mist/50">
                    Skipped (unsupported): {scanCode.data.unsupported.map((u) => `${u.ext} ×${u.files}`).join(' · ')}
                  </div>
                )}
                {/* One-flow "add a repo as a source": target project (existing
                    or created on the spot), scan type, optional git watcher. */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-ink dark:text-mist">
                  <label className="inline-flex items-center gap-1">
                    Target
                    <select
                      value={importTarget}
                      onChange={(e) => setImportTarget(e.target.value)}
                      className="h-7 rounded-md px-1.5 bg-white border border-parchment-border dark:bg-midnight-700 dark:border-midnight-600"
                    >
                      <option value="current">project „{project}"</option>
                      <option value="new">new project…</option>
                    </select>
                  </label>
                  {importTarget === 'new' && (
                    <input
                      value={newProjName}
                      onChange={(e) => setNewProjName(e.target.value)}
                      placeholder="project name"
                      className="h-7 rounded-md px-2 bg-white border border-parchment-border dark:bg-midnight-700 dark:border-midnight-600"
                    />
                  )}
                  <label className="inline-flex items-center gap-1 opacity-70" title="Everything supported always lands in the vector index">
                    <input type="checkbox" checked disabled className="accent-gold" /> Vector
                  </label>
                  <label
                    className="inline-flex items-center gap-1"
                    title="Build the Neo4j code graph + bind the repo (Graph RAG, trace, ripple)"
                  >
                    <input
                      type="checkbox"
                      checked={optGraph}
                      disabled={!scanCode.data.has_graph_lang}
                      onChange={(e) => setOptGraph(e.target.checked)}
                      className="accent-gold"
                    />
                    Code graph{!scanCode.data.has_graph_lang && ' (no analyzer language)'}
                  </label>
                  <label
                    className="inline-flex items-center gap-1"
                    title="Register the git watcher: committed changes sync automatically (hysteresis-gated)"
                  >
                    <input
                      type="checkbox"
                      checked={optWatch}
                      disabled={!scanCode.data.git?.is_git}
                      onChange={(e) => setOptWatch(e.target.checked)}
                      className="accent-gold"
                    />
                    Auto-sync watcher{!scanCode.data.git?.is_git && ' (not a git repo)'}
                    {scanCode.data.git?.is_git && (
                      <span className="font-mono text-ink-soft dark:text-mist/50">
                        {scanCode.data.git.branch}@{scanCode.data.git.head?.slice(0, 7)}
                      </span>
                    )}
                  </label>
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const target = importTarget === 'new' ? newProjName.trim() : project
                    if (!target) return
                    importCode.mutate(
                      {
                        project: target,
                        path: scanCode.data.root,
                        graph: optGraph,
                        auto_sync: optWatch,
                        create_project: importTarget === 'new',
                      },
                      { onSuccess: (r) => setCodeSagaId(r.saga_id) }
                    )
                  }}
                  loading={importCode.isPending}
                  disabled={scanCode.data.total_files === 0 || (importTarget === 'new' ? !newProjName.trim() : !project)}
                >
                  Import codebase into {importTarget === 'new' ? (newProjName.trim() || '…') : project}
                </Button>
                {importCode.isError && <Note kind="error" text={importCode.error?.message} hint={importCode.error?.hint} />}
              </div>
            )}
            {/* Durable import progress: the saga runs server-side (survives a
                closed tab and BFF restarts) — this just watches it. */}
            {codeSagaId && <CodeImportProgress saga={codeSaga.data} />}
            {/* Git-aware sync of an ALREADY indexed repo: the exact delta since
                the recorded baseline (committed + working tree) — no false
                re-embeds after branch switches, deletions really purge. */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (!codePathInput.trim()) return
                  setSyncSagaId(null)
                  gitStatus.mutate({ project, path: codePathInput.trim() })
                }}
                disabled={!codePathInput.trim() || !project || gitStatus.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
                title="Show what changed in git since the last sync (committed + working tree) — nothing is indexed yet"
              >
                {gitStatus.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                Check git changes
              </button>
              {gitStatus.isSuccess && gitStatus.data.is_git === false && (
                <span className="text-xs text-ink-soft dark:text-mist/60">not a git repository — use Refresh codebase below</span>
              )}
              {gitStatus.isSuccess && gitStatus.data.needs_full && (
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  baseline unusable ({gitStatus.data.reason}) — run Refresh codebase once, then Sync
                </span>
              )}
              {gitStatus.isSuccess && gitStatus.data.is_git && !gitStatus.data.needs_full && (
                <>
                  <span className="text-xs font-mono text-ink-soft dark:text-mist/60" title={gitStatus.data.head}>
                    {gitStatus.data.branch} @ {gitStatus.data.head?.slice(0, 7)}
                  </span>
                  <label
                    className="inline-flex items-center gap-1 text-xs text-ink-soft dark:text-mist/60 cursor-pointer"
                    title="Poller watches this repo: committed changes sync automatically (quiet period, min batch, staleness ceiling, cap — see Settings docs)"
                  >
                    <input
                      type="checkbox"
                      className="accent-gold"
                      checked={autoSync.data?.auto_sync ?? gitStatus.data.auto_sync ?? false}
                      disabled={autoSync.isPending || !gitStatus.data.baseline}
                      onChange={(e) =>
                        autoSync.mutate({ project, path: codePathInput.trim(), enabled: e.target.checked })
                      }
                    />
                    Auto-sync
                  </label>
                  {gitStatus.data.total_changes === 0 && gitStatus.data.baseline ? (
                    <>
                      <span className="text-xs text-emerald-700 dark:text-emerald-300">up to date with baseline</span>
                      <button
                        type="button"
                        onClick={() =>
                          syncCode.mutate(
                            { project, path: codePathInput.trim(), full: true },
                            { onSuccess: (r) => setSyncSagaId(r.saga_id) }
                          )
                        }
                        disabled={syncCode.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
                        title="Reconcile the INDEX against the disk (mtime diff), ignoring the git baseline — use when the baseline was recorded after the commits the index is missing"
                      >
                        Full reconcile
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-ink dark:text-mist">
                        {gitStatus.data.added > 0 && `+${gitStatus.data.added} `}
                        {gitStatus.data.modified > 0 && `~${gitStatus.data.modified} `}
                        {gitStatus.data.deleted > 0 && `−${gitStatus.data.deleted} `}
                        {!gitStatus.data.baseline && '(no baseline yet — first sync records one)'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          syncCode.mutate(
                            { project, path: codePathInput.trim() },
                            { onSuccess: (r) => setSyncSagaId(r.saga_id) }
                          )
                        }
                        disabled={syncCode.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gold/60 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
                        title="Apply exactly this delta: upsert changed/new files, purge deleted ones, rebuild the graph if a code language was touched"
                      >
                        {syncCode.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        Sync now
                      </button>
                    </>
                  )}
                </>
              )}
              {gitStatus.isError && <span className="text-xs text-red-600">{gitStatus.error?.message}</span>}
              {syncCode.isError && <span className="text-xs text-red-600">{syncCode.error?.message}</span>}
            </div>
            {syncSagaId && <CodeImportProgress saga={syncSaga.data} />}
            {/* Re-sync a previously indexed codebase: changed files re-embed,
                new files add, missing report — never duplicates. */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => codePathInput.trim() && refreshCode.mutate({ project, path: codePathInput.trim(), kind: 'code' })}
                disabled={!codePathInput.trim() || !project || refreshCode.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
                title="Diff re-sync: re-index changed files, add new ones, report missing (no duplicates)"
              >
                {refreshCode.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                Refresh codebase
              </button>
              {refreshCode.isSuccess && (
                <span className="text-xs text-emerald-700 dark:text-emerald-300">
                  {refreshCode.data.reindexed} re-indexed · {refreshCode.data.added} new · {refreshCode.data.unchanged} fresh
                  {refreshCode.data.missing?.length > 0 && ` · ${refreshCode.data.missing.length} missing`}
                </span>
              )}
              {refreshCode.isError && <span className="text-xs text-red-600">{refreshCode.error?.message}</span>}
            </div>
            {/* Optional structural layer: load the repo into the Neo4j code
                graph → unlocks the "Graph RAG" strategy in Ask (imports/
                extends/impact questions). Python + Java. */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => codePathInput.trim() && buildGraph.mutate({ repo_path: codePathInput.trim(), project })}
                disabled={!codePathInput.trim() || !project || buildGraph.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
                title="Build the Neo4j code graph for this repo and bind it to this project — enables & scopes the 'Graph RAG' strategy in Ask (needs Neo4j running)"
              >
                {buildGraph.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                Build code graph
              </button>
              {buildGraph.isSuccess && (
                <span className="text-xs text-emerald-700 dark:text-emerald-300">
                  {buildGraph.data.nodes} nodes / {buildGraph.data.relationships} relations → Ask strategy “Graph RAG”
                </span>
              )}
              {buildGraph.isError && <span className="text-xs text-red-600">{buildGraph.error?.message}</span>}
            </div>
            {/* Graph RAG scope: repos bound to this project. Structural Ask
                answers (Graph RAG) are restricted to these — so a same-named
                class/method in another repo can't leak in. */}
            {(codeReposQ.data?.code_repos?.length ?? 0) > 0 && (
              <div className="pt-1 border-t border-parchment-border/60 dark:border-midnight-600/60">
                <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1">
                  Graph RAG scope — {project}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {codeReposQ.data.code_repos.map((repo) => (
                    <span key={repo} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/10 text-gold-deep dark:text-gold text-xs font-mono" title={repo}>
                      <button
                        type="button"
                        onClick={() => setCodePathInput(repo)}
                        className="hover:underline underline-offset-2"
                        title={`Use ${repo} in the path field (Check git changes / Sync)`}
                      >
                        {repo.split(/[\\/]/).filter(Boolean).slice(-1)[0] || repo}
                      </button>
                      <button
                        onClick={() => unbindRepo.mutate({ project, repo_path: repo })}
                        className="hover:text-red-600" aria-label="Unbind repo" title="Unbind (widen Graph RAG scope)"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* F2 — Confluence connector (renders only when configured in bff/.env) */}
          <ConfluenceImport project={project} />
        </>
      )}

      {project && view !== 'add' && (
        <section className="space-y-3">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg text-ink dark:text-mist">
              {docsQuery.data?.total ?? docsQuery.data?.count ?? 0} documents in <span className="text-gold-deep dark:text-gold">{project}</span>
            </h2>
            <RefreshStaleButton project={project} />
            <LinkHealthButton project={project} />
          </div>

          {docsQuery.isLoading && <Note kind="muted" text="Loading documents…" />}
          {docsQuery.isError && <Note kind="error" text={docsQuery.error?.message} hint={docsQuery.error?.hint} />}
          {!docsQuery.isLoading && documents.length === 0 && (
            <Note kind="muted" text="No documents indexed yet. Use Add to bring some in." />
          )}

          {documents.length > 0 && (
            <ul className="divide-y divide-parchment-border dark:divide-midnight-600 rounded-xl border border-parchment-border dark:border-midnight-600 overflow-hidden">
              {documents.map((d) => (
                <li
                  key={d.source}
                  onClick={() => setViewer({ source: d.source })}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-parchment-200/60 dark:hover:bg-midnight-700/60 ${
                    d.curated_excluded ? 'bg-white/30 opacity-55 dark:bg-midnight-800/25' : 'bg-white/60 dark:bg-midnight-800/50'
                  }`}
                  title={d.curated_excluded ? `Vyřazeno z retrievalu${d.curation_note ? ` — ${d.curation_note}` : ''}` : 'View document'}
                >
                  <FileText className="w-4 h-4 shrink-0 text-ink-soft dark:text-mist/50" />
                  <span className={`flex-1 truncate text-sm ${d.curated_excluded ? 'line-through text-ink-soft dark:text-mist/50' : 'text-ink dark:text-mist'}`} title={d.source}>{basename(d.source)}</span>
                  {d.language && <span className="text-xs uppercase text-ink-soft dark:text-mist/50">{d.language}</span>}
                  <span className="text-xs text-ink-soft dark:text-mist/50 w-20 text-right">{d.chunks} chunks</span>
                  <button
                    onClick={(ev) => {
                      ev.stopPropagation()
                      curateDoc.mutate({
                        file_path: d.source, project,
                        action: d.curated_excluded ? 'include' : 'exclude',
                        note: d.curated_excluded ? undefined
                          : (window.prompt('Poznámka (proč vyřadit — volitelné):') || undefined),
                      })
                    }}
                    disabled={curateDoc.isPending}
                    aria-label={d.curated_excluded ? 'Include in retrieval' : 'Exclude from retrieval'}
                    title={d.curated_excluded
                      ? 'Vrátit do retrievalu (search/ask ho zase uvidí)'
                      : 'Vyřadit z retrievalu — dokument zůstane, ale search/ask/truth-lens/impact ho přestanou používat'}
                    className="p-1.5 rounded-md text-ink-soft hover:text-gold-deep hover:bg-gold/10 dark:text-mist/50 dark:hover:text-gold"
                  >
                    {d.curated_excluded ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); reindexDoc.mutate({ file_path: d.source, project }) }}
                    disabled={reindexDoc.isPending}
                    aria-label="Re-index document"
                    title="Re-index from the source file (drops old chunks, re-embeds)"
                    className="p-1.5 rounded-md text-ink-soft hover:text-gold-deep hover:bg-gold/10 dark:text-mist/50 dark:hover:text-gold"
                  >
                    <RotateCw className={`w-4 h-4 ${reindexDoc.isPending && reindexDoc.variables?.file_path === d.source ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); removeDoc.mutate({ file_path: d.source, project }) }}
                    disabled={removeDoc.isPending}
                    aria-label="Delete document"
                    className="p-1.5 rounded-md text-ink-soft hover:text-red-600 hover:bg-red-50 dark:text-mist/50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {(docsQuery.data?.total ?? 0) > DOC_PAGE && (
            <div className="flex items-center justify-between text-sm text-ink-soft dark:text-mist/50 pt-1">
              <span>
                {docOffset + 1}–{docOffset + documents.length} of {docsQuery.data.total}
                {docsQuery.isFetching && <span className="ml-2 opacity-60">…</span>}
              </span>
              <span className="inline-flex gap-2">
                <button
                  onClick={() => setDocOffset((o) => Math.max(0, o - DOC_PAGE))}
                  disabled={docOffset === 0 || docsQuery.isFetching}
                  className="h-8 px-3 rounded-md border border-parchment-border text-xs disabled:opacity-40 hover:border-gold dark:border-midnight-600"
                >Prev</button>
                <button
                  onClick={() => setDocOffset((o) => o + DOC_PAGE)}
                  disabled={docOffset + DOC_PAGE >= (docsQuery.data?.total ?? 0) || docsQuery.isFetching}
                  className="h-8 px-3 rounded-md border border-parchment-border text-xs disabled:opacity-40 hover:border-gold dark:border-midnight-600"
                >Next</button>
              </span>
            </div>
          )}
          {removeDoc.isError && <Note kind="error" text={removeDoc.error?.message} hint={removeDoc.error?.hint} />}
        </section>
      )}

      {viewer && <DocumentViewer project={project} source={viewer.source} onClose={() => setViewer(null)} />}
    </div>
  )
}

function basename(p) {
  if (!p) return '—'
  const parts = p.split(/[\\/]/)
  return parts[parts.length - 1] || p
}

function fmtBytes(n) {
  if (!n) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

// Progress of a codebase saga (index-codebase / sync-codebase): chips render
// from the saga's actual steps, the vector step's intermediate data carries
// cursor/total for the bar. Runs server-side; closing the tab does not stop it.
const STEP_LABELS = {
  scan: 'Scan',
  detect: 'Detect (git)',
  vector: 'Vector index',
  bind: 'Bind repo',
  graph: 'Code graph',
  baseline: 'Baseline',
}

function CodeImportProgress({ saga }) {
  if (!saga) return <p className="text-xs text-ink-soft dark:text-mist/60">Starting…</p>
  const steps = saga.steps ?? []
  const byName = Object.fromEntries(steps.map((s) => [s.name, s]))
  const vd = byName.vector?.data
  const running = saga.status === 'running'
  const pct = vd?.total ? Math.min(100, Math.round(((vd.cursor ?? 0) / vd.total) * 100)) : null
  const chip = (s) => {
    const cls =
      s.status === 'done'
        ? 'text-emerald-700 dark:text-emerald-300'
        : s.status === 'running'
          ? 'text-gold-deep dark:text-gold'
          : s.status === 'failed'
            ? 'text-red-600 dark:text-red-400'
            : 'text-ink-soft/60 dark:text-mist/40'
    const label =
      s.name === 'vector' && s.status === 'running' && vd?.total
        ? `${STEP_LABELS.vector} ${vd.cursor ?? 0}/${vd.total}`
        : (STEP_LABELS[s.name] ?? s.name)
    return (
      <span key={s.name} className={`inline-flex items-center gap-1 text-xs ${cls}`}>
        {s.status === 'running' && <Loader2 className="w-3 h-3 animate-spin" />}
        {label}
      </span>
    )
  }
  // vector done-data differs per saga type: import reports files/chunks,
  // sync reports upserts/deletes.
  const done = byName.vector?.status === 'done' ? byName.vector.data : null
  const summary = done
    ? done.indexed_files != null
      ? `Indexed ${done.chunks_indexed} code chunks from ${done.indexed_files} files`
      : `Synced ${done.upserts} upsert(s), ${done.deletes} delete(s)`
    : null
  const graphNote = byName.graph?.data?.nodes
    ? ` · graph: ${byName.graph.data.nodes} nodes / ${byName.graph.data.relationships} relations`
    : byName.graph?.data?.skipped
      ? ` · graph: ${byName.graph.data.skipped}`
      : ''
  return (
    <div className="rounded-lg border border-parchment-border/70 dark:border-midnight-600/70 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {steps.map((s, i) => (
          <React.Fragment key={s.name}>
            {i > 0 && <span className="text-ink-soft/40">→</span>}
            {chip(s)}
          </React.Fragment>
        ))}
      </div>
      {running && pct != null && (
        <div className="h-1.5 rounded-full bg-parchment-border/60 dark:bg-midnight-600/60 overflow-hidden">
          <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      {vd?.last_error && running && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">Retrying after: {vd.last_error}</p>
      )}
      {saga.status === 'completed' && summary && <Note kind="ok" text={`${summary}${graphNote}.`} />}
      {(saga.status === 'failed' || saga.status === 'compensated') && (
        <Note kind="error" text={`Failed: ${saga.error ?? 'unknown error'}`} hint="Already-indexed files stay in the project; re-running continues from a fresh detection." />
      )}
      {saga.status === 'cancelled' && <Note kind="muted" text="Cancelled — already-indexed files stay in the project." />}
    </div>
  )
}

function Note({ kind, text, hint }) {
  const styles = {
    ok: 'border-emerald-300/70 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300',
    error: 'border-red-300/70 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-300',
    muted: 'border-parchment-border bg-white/60 text-ink-soft dark:border-midnight-600 dark:bg-midnight-800/50 dark:text-mist/60',
  }
  const Icon = kind === 'ok' ? CheckCircle2 : kind === 'error' ? AlertTriangle : null
  return (
    <div className={`rounded-xl border p-4 text-sm ${styles[kind]}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        <span>{text}</span>
      </div>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  )
}
